import React from 'react';

const S = {
  card: {
    background: '#131720', border: '1px solid #1e2736',
    borderRadius: 12, padding: '16px 20px',
  },
  metricCard: {
    background: '#0f1319', border: '1px solid #1a2030',
    borderRadius: 8, padding: '14px 16px',
  },
};

export function Card({ children, style, className }) {
  return <div style={{ ...S.card, ...style }} className={className}>{children}</div>;
}

export function MetricCard({ label, value, sub, valueColor }) {
  return (
    <div style={S.metricCard}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: valueColor || '#e2e8f0', fontFamily: "'IBM Plex Mono', monospace" }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export function Badge({ children, type = 'neutral' }) {
  const colors = {
    buy:     { bg: 'rgba(16,185,129,0.12)', color: '#10b981', border: 'rgba(16,185,129,0.25)' },
    sell:    { bg: 'rgba(239,68,68,0.12)',  color: '#f87171', border: 'rgba(239,68,68,0.25)' },
    neutral: { bg: 'rgba(100,116,139,0.12)',color: '#94a3b8', border: 'rgba(100,116,139,0.25)' },
    positive:{ bg: 'rgba(16,185,129,0.12)', color: '#10b981', border: 'rgba(16,185,129,0.25)' },
    negative:{ bg: 'rgba(239,68,68,0.12)',  color: '#f87171', border: 'rgba(239,68,68,0.25)' },
    warning: { bg: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: 'rgba(245,158,11,0.25)' },
  };
  const c = colors[type] || colors.neutral;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      letterSpacing: '0.04em',
    }}>{children}</span>
  );
}

export function ScoreBar({ label, score, color }) {
  const col = color || (score >= 65 ? '#10b981' : score >= 45 ? '#fbbf24' : '#f87171');
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 13 }}>
        <span style={{ color: '#94a3b8' }}>{label}</span>
        <span style={{ color: col, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{score}/100</span>
      </div>
      <div style={{ height: 5, background: '#1e2736', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', background: col, borderRadius: 3, transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );
}

export function Spinner({ size = 20 }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
      <div style={{
        width: size, height: size, borderRadius: '50%',
        border: `2px solid #1e2736`, borderTopColor: '#3b82f6',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function ErrorMsg({ msg }) {
  return (
    <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#f87171', fontSize: 13 }}>
      ⚠ {msg}
    </div>
  );
}

export function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
      {children}
    </div>
  );
}

export function fmt(n, decimals = 2) {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(0)}M`;
  return n.toFixed(decimals);
}

const CURRENCY_SYMBOLS = {
  EUR: '€', USD: '$', GBP: '£', GBp: 'p', CHF: 'CHF ', JPY: '¥',
};

// Restituisce il simbolo di valuta (prefisso) per un codice ISO
export function curSym(currency) {
  return CURRENCY_SYMBOLS[currency] || (currency ? currency + ' ' : '€');
}

// Formatta un prezzo con la valuta corretta.
// GBp (pence di Londra) viene mostrato come suffisso "p".
export function fmtPrice(value, currency = 'EUR', decimals = 2) {
  if (value == null) return '—';
  const v = Number(value).toFixed(decimals);
  if (currency === 'GBp') return `${v} p`;
  return `${curSym(currency)}${v}`;
}

// Formatta valori grandi (market cap, ricavi) con valuta
export function fmtBig(value, currency = 'EUR') {
  if (value == null) return '—';
  const sym = currency === 'GBp' ? '£' : curSym(currency);
  return `${sym}${fmt(value)}`;
}

export function fmtPct(n) {
  if (n == null) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}

export function changeColor(n) {
  if (!n) return '#94a3b8';
  return n > 0 ? '#10b981' : '#f87171';
}
